import { signOut } from '@aws-amplify/auth';
import { CaretDownIcon, CaretUpIcon, ChatTextIcon, FileArrowDownIcon, GearIcon, RulerIcon, TextTIcon } from '@phosphor-icons/react';
import { LogOutIcon } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import CalendarIcon from '@/assets/svgs/Calendar';
import GcrIcon from '@/assets/svgs/Gcr';
import MaskIcon from '@/assets/svgs/Mask';
import ModelIcon from '@/assets/svgs/Model';
import OverlayIcon from '@/assets/svgs/Overlay';
import StyleIcon from '@/assets/svgs/Style';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Routes } from '@/router/routes';

import { LocalStorageKeys } from '../constants';
import { useCurrentUser, useTableStore } from '../store';

const items = [
  {
    title: 'Models',
    url: Routes.Models,
    icon: <ModelIcon />,
  },
  {
    title: 'Model Sizes',
    url: Routes.ModelSizes,
    icon: <RulerIcon size={24} />,
  },
  {
    title: 'Styles',
    url: Routes.Styles,
    icon: <StyleIcon />,
  },
  {
    title: 'Text Styles',
    url: Routes.TextStyles,
    icon: <TextTIcon size={24} />,
  },
  {
    title: 'Masks',
    url: Routes.Masks,
    icon: <MaskIcon />,
  },
  {
    title: 'GCR Files',
    url: Routes.Gcrs,
    icon: <GcrIcon />,
  },
  {
    title: 'Overlays',
    url: Routes.Overlays,
    icon: <OverlayIcon />,
  },
  {
    title: 'Events',
    url: Routes.Events,
    icon: <CalendarIcon />,
  },
  {
    title: 'Instances',
    url: Routes.InstancesManager,
    icon: <GearIcon size={24} />,
  },
  {
    title: 'System Prompts',
    url: Routes.SystemPrompts,
    icon: <ChatTextIcon size={24} />,
  },
  {
    title: 'SMS Templates',
    url: Routes.NotificationTemplates,
    icon: <ChatTextIcon size={24} />,
  },
  {
    title: 'Downloads',
    url: Routes.Downloads,
    icon: <FileArrowDownIcon size={24} />,
  },
];

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { open } = useSidebar();
  const { resetTableState } = useTableStore();

  const { currentUser, setCurrentUser } = useCurrentUser();

  const activeUrl = items
    .filter(
      ({ url }) =>
        location.pathname === url || location.pathname.startsWith(`${url}/`)
    )
    .sort((a, b) => b.url.length - a.url.length)[0]?.url;

  const logoutUser = async () => {
    try {
      await signOut();
      localStorage.removeItem(LocalStorageKeys.AccessToken);
      setCurrentUser(null);
      navigate(Routes.SignIn);
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Sidebar collapsible="icon" data-testid="sidebar" className="z-50">
      <SidebarHeader className="items-center">
        <div className="flex items-center justify-between w-full">
          <h1 className="text-lg font-bold text-zinc-50">
            {open ? '1OF1 Admin' : ''}
          </h1>
          <SidebarTrigger className="hover:bg-transparent hover:text-accent-foreground active:bg-transparent hover:opacity-70" />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.title} onClick={resetTableState}>
                  <SidebarMenuButton
                    asChild
                    size="lg"
                    data-testid={`sidebar-menu-item-${item.title?.toLowerCase()}`}
                    isActive={item.url === activeUrl}
                    className={open ? '' : 'justify-center'}
                  >
                    <NavLink to={item.url}>
                      {item.icon}
                      {open && (
                        <span className="text-base font-semibold">
                          {item.title}
                        </span>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            {currentUser?.userId &&
              (!open ? (
                <SidebarMenuButton
                  asChild
                  size="lg"
                  className={'justify-center'}
                >
                  <Button
                    variant="ghost"
                    data-testid="logout"
                    onClick={logoutUser}
                  >
                    <LogOutIcon />
                  </Button>
                </SidebarMenuButton>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div
                      className="w-full flex items-center cursor-pointer gap-2"
                      data-testid="header-user-info"
                    >
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-zinc-100 text-sm font-medium">
                          Administrator
                        </span>
                        <span className="text-zinc-500 text-sm truncate">
                          {currentUser.signInDetails.loginId}
                        </span>
                      </div>
                      <CaretDownIcon
                        color={colors.zinc[500]}
                        size={20}
                        className="shrink-0"
                      />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        data-testid="logout"
                        onClick={logoutUser}
                      >
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              ))}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
